import { useMemo } from 'react';
import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { router, useLocalSearchParams } from 'expo-router';
import { theme } from '@/constants/theme';
import { buildRuntimeExamPath, buildRuntimeHomePath, buildRuntimePracticePath, buildRuntimeReviewPath, type RuntimeBase } from '@/features/unified-learning/runtime/runtimeRoutes';

const BG = '#0B0B14';
const SURFACE = '#16142A';
const BORDER = '#2D2B50';
const TEXT = '#F0EFF8';
const TEXT_MED = '#9896B4';
const PRIMARY = theme.colors.primary;

const readinessCopy = {
  ready: {
    title: 'מוכן להתקדם ✓',
    body: 'התוצאות מראות מוכנות טובה. המשך לשיעור הבא או בצע מבחן נוסף לאימות.',
    tone: '#34D399',
  },
  almost_ready: {
    title: 'כמעט מוכן',
    body: 'יש התקדמות טובה, אבל עדיין יש אזורים חלשים שצריך לחזק.',
    tone: '#FBBF24',
  },
  needs_practice: {
    title: 'נדרש תרגול נוסף',
    body: 'כרגע המוכנות עדיין לא יציבה. כדאי לחזור לתרגול חולשות ולסקירת טעויות.',
    tone: '#F87171',
  },
} as const;

export function RuntimeExamResultScreen({ runtimeBase }: { runtimeBase: RuntimeBase }) {
  const {
    slug, uid, token,
    total, answered, xp,
    strong, acceptable, weak, risky,
    weak_lessons, strong_lessons, readiness,
  } = useLocalSearchParams<{
    slug: string;
    uid: string;
    token?: string;
    total?: string;
    answered?: string;
    xp?: string;
    strong?: string;
    acceptable?: string;
    weak?: string;
    risky?: string;
    weak_lessons?: string;
    strong_lessons?: string;
    readiness?: 'ready' | 'almost_ready' | 'needs_practice';
  }>();

  // Safe type guards — expo-router can return string[]
  const safeSlug  = typeof slug  === 'string' ? slug  : '';
  const safeUid   = typeof uid   === 'string' ? uid   : '';
  const safeToken = typeof token === 'string' ? token : undefined;
  const runtimeSession = { uid: safeUid, token: safeToken };

  const totals = {
    total:      Number(total      ?? 0),
    answered:   Number(answered   ?? 0),
    xp:         Number(xp         ?? 0),
    strong:     Number(strong     ?? 0),
    acceptable: Number(acceptable ?? 0),
    weak:       Number(weak       ?? 0),
    risky:      Number(risky      ?? 0),
  };

  const readinessKey = (typeof readiness === 'string' && readiness in readinessCopy)
    ? readiness as keyof typeof readinessCopy
    : 'needs_practice';
  const readinessView = readinessCopy[readinessKey];

  const weakLessons   = useMemo(() => String(weak_lessons   ?? '').split(',').map((x) => x.trim()).filter(Boolean), [weak_lessons]);
  const strongLessons = useMemo(() => String(strong_lessons ?? '').split(',').map((x) => x.trim()).filter(Boolean), [strong_lessons]);

  return (
    <ScrollView style={s.root} contentContainerStyle={s.content}>
      <Text style={s.eyebrow}>KURSI LEARN · מוכנות</Text>
      <Text style={s.title}>תוצאות המבחן</Text>

      <View style={[s.readinessCard, { borderColor: readinessView.tone + '66' }]}>
        <Text style={[s.readinessTitle, { color: readinessView.tone }]}>{readinessView.title}</Text>
        <Text style={s.readinessBody}>{readinessView.body}</Text>
      </View>

      <View style={s.card}>
        <Text style={s.cardTitle}>סיכום הסשן</Text>
        <Text style={s.row}>ענה: {totals.answered}/{totals.total}</Text>
        <Text style={s.row}>XP הרוויח: {totals.xp}</Text>
        <Text style={s.row}>חזק: {totals.strong} · סביר: {totals.acceptable}</Text>
        <Text style={s.row}>חלש: {totals.weak} · מסוכן: {totals.risky}</Text>
      </View>

      <View style={s.card}>
        <Text style={s.cardTitle}>אזורים לחיזוק</Text>
        {weakLessons.length === 0 ? (
          <Text style={s.row}>לא זוהו שיעורים חלשים ספציפיים בסשן זה.</Text>
        ) : (
          <Text style={s.row}>
            {weakLessons.length === 1 ? 'שיעור אחד' : `${weakLessons.length} שיעורים`} זוהו כחלשים — תרגול ממוקד יתמקד בהם.
          </Text>
        )}
      </View>

      {strongLessons.length > 0 && (
        <View style={s.card}>
          <Text style={s.cardTitle}>אזורים חזקים</Text>
          <Text style={s.row}>
            {strongLessons.length === 1 ? 'שיעור אחד' : `${strongLessons.length} שיעורים`} הוצגו בביצוע טוב בסשן זה.
          </Text>
        </View>
      )}

      <View style={s.actionGroup}>
        <Pressable
          style={s.primaryBtn}
          onPress={() => router.push(buildRuntimePracticePath(runtimeBase, safeSlug, runtimeSession) as any)}
          disabled={!safeSlug || !safeUid}
        >
          <Text style={s.primaryBtnText}>תרגל אזורי חולשה</Text>
        </Pressable>

        <Pressable
          style={s.secondaryBtn}
          onPress={() => router.push(buildRuntimeReviewPath(runtimeBase, safeSlug, runtimeSession) as any)}
          disabled={!safeSlug || !safeUid}
        >
          <Text style={s.secondaryBtnText}>פתח סקירת טעויות</Text>
        </Pressable>

        <Pressable
          style={s.secondaryBtn}
          onPress={() => router.push(buildRuntimeExamPath(runtimeBase, safeSlug, runtimeSession) as any)}
          disabled={!safeSlug || !safeUid}
        >
          <Text style={s.secondaryBtnText}>מבחן נוסף</Text>
        </Pressable>

        <Pressable
          style={s.homeBtn}
          onPress={() => router.replace(safeSlug && safeUid
            ? buildRuntimeHomePath(runtimeBase, safeSlug, runtimeSession) as any
            : '/learn' as any)}
        >
          <Text style={s.homeBtnText}>חזרה לבית המסלול</Text>
        </Pressable>
      </View>
    </ScrollView>
  );
}

const s = StyleSheet.create({
  root: { flex: 1, backgroundColor: BG },
  content: { padding: 20, gap: 14, maxWidth: 700, width: '100%', alignSelf: 'center' as any, paddingTop: 42, paddingBottom: 40 },
  eyebrow: { color: PRIMARY, fontFamily: theme.fonts.bold, fontSize: 12, letterSpacing: 1 },
  title: { color: TEXT, fontFamily: theme.fonts.bold, fontSize: 30, textAlign: 'right' },
  readinessCard: { backgroundColor: SURFACE, borderWidth: 1, borderRadius: 14, padding: 14, gap: 8 },
  readinessTitle: { fontFamily: theme.fonts.bold, fontSize: 20, textAlign: 'right' },
  readinessBody: { color: TEXT_MED, fontFamily: theme.fonts.regular, textAlign: 'right' },
  card: { backgroundColor: SURFACE, borderWidth: 1, borderColor: BORDER, borderRadius: 14, padding: 14, gap: 8 },
  cardTitle: { color: TEXT, fontFamily: theme.fonts.bold, fontSize: 18, textAlign: 'right' },
  row: { color: TEXT_MED, fontFamily: theme.fonts.regular, textAlign: 'right' },
  actionGroup: { gap: 8 },
  primaryBtn: { backgroundColor: PRIMARY, borderRadius: 12, alignItems: 'center', paddingVertical: 12 },
  primaryBtnText: { color: '#fff', fontFamily: theme.fonts.bold, fontSize: 16 },
  secondaryBtn: { borderWidth: 1, borderColor: BORDER, borderRadius: 12, alignItems: 'center', paddingVertical: 11 },
  secondaryBtnText: { color: TEXT, fontFamily: theme.fonts.bold },
  homeBtn: { borderRadius: 12, alignItems: 'center', paddingVertical: 11 },
  homeBtnText: { color: TEXT_MED, fontFamily: theme.fonts.regular, fontSize: 14 },
});
